'use client';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { MapPin, Info, Search, X, ChevronRight, Landmark, TreePine, Users, BookOpen, Filter, Globe2, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { PROVINCES, Province } from './chiefdomsData';
import { fetchChieftainships, ChieftainshipRow } from '@/lib/db/chieftainships';

// ─── Province colour map ──────────────────────────────────────────────────────
const PROVINCE_COLORS: Record<string, { fill: string; text: string; border: string }> = {
  'mashonaland-west':    { fill: '#FDF3E7', text: '#8B4513', border: '#C8860A' },
  'mashonaland-central': { fill: '#F0F5E8', text: '#3A5A1A', border: '#5A8A2A' },
  'mashonaland-east':    { fill: '#E8F5EE', text: '#1A5A3A', border: '#2A8A5A' },
  'manicaland':          { fill: '#FDE8E8', text: '#7A0000', border: '#AA2020' },
  'masvingo':            { fill: '#FDF8E1', text: '#8B6914', border: '#C8A020' },
  'midlands':            { fill: '#E8EEF8', text: '#2A4A7A', border: '#4A6AAA' },
  'matabeleland-north':  { fill: '#F3E8F8', text: '#5A1A7A', border: '#8A3AAA' },
  'matabeleland-south':  { fill: '#FDF0D8', text: '#7A5A0A', border: '#AA8020' },
  'harare':              { fill: '#FFE8E8', text: '#CC0000', border: '#CC0000' },
  'bulawayo':            { fill: '#FFE8E0', text: '#CC5500', border: '#CC5500' },
};

const PROVINCE_SHAPES: Record<string, { points: string; labelX: number; labelY: number }> = {
  'mashonaland-west':    { points: '60,30 200,25 215,85 195,145 135,162 78,152 48,102', labelX: 120, labelY: 90 },
  'mashonaland-central': { points: '200,25 345,18 362,92 318,152 215,162 195,145 215,85', labelX: 278, labelY: 85 },
  'mashonaland-east':    { points: '345,18 462,28 472,132 402,202 318,192 318,152 362,92', labelX: 390, labelY: 95 },
  'manicaland':          { points: '462,28 562,58 572,202 482,282 402,262 402,202 472,132', labelX: 490, labelY: 140 },
  'masvingo':            { points: '318,192 482,282 472,402 358,432 238,402 218,302 278,242', labelX: 355, labelY: 320 },
  'midlands':            { points: '135,162 318,152 318,192 278,242 218,302 158,282 98,222 98,182', labelX: 210, labelY: 215 },
  'matabeleland-north':  { points: '48,102 135,162 98,182 98,282 58,302 18,242 18,142', labelX: 68, labelY: 200 },
  'matabeleland-south':  { points: '58,302 158,282 218,302 238,402 198,462 98,452 48,382', labelX: 148, labelY: 380 },
  'harare':              { points: '288,148 322,148 322,178 288,178', labelX: 305, labelY: 163 },
  'bulawayo':            { points: '108,298 142,298 142,322 108,322', labelX: 125, labelY: 310 },
};

// Map province name → province id for colour lookup
const PROVINCE_NAME_TO_ID: Record<string, string> = {
  'Mashonaland West':    'mashonaland-west',
  'Mashonaland Central': 'mashonaland-central',
  'Mashonaland East':    'mashonaland-east',
  'Manicaland':          'manicaland',
  'Masvingo':            'masvingo',
  'Midlands':            'midlands',
  'Matabeleland North':  'matabeleland-north',
  'Matabeleland South':  'matabeleland-south',
  'Harare Metropolitan': 'harare',
  'Bulawayo Metropolitan': 'bulawayo',
};

export default function ChieflyDomainsPage() {
  const [chieftainships, setChieftainships] = useState<ChieftainshipRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedProvince, setSelectedProvince] = useState<string | null>(null);
  const [selectedChiefdom, setSelectedChiefdom] = useState<ChieftainshipRow | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterProvince, setFilterProvince] = useState<string>('all');
  const [hoveredProvince, setHoveredProvince] = useState<string | null>(null);

  const loadChieftainships = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchChieftainships({ verified: true });
      setChieftainships(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load chieftainships');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadChieftainships();
  }, [loadChieftainships]);

  const filteredChiefdoms = useMemo(() => {
    return chieftainships.filter(c => {
      const matchesSearch =
        !searchQuery ||
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.district.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.province.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.clan_lineage.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesProvince =
        filterProvince === 'all' ||
        PROVINCES.find(p => p.id === filterProvince)?.name === c.province;
      return matchesSearch && matchesProvince;
    });
  }, [chieftainships, searchQuery, filterProvince]);

  const activeProvince = selectedProvince
    ? PROVINCES.find(p => p.id === selectedProvince)
    : null;

  const provinceChiefdoms = activeProvince
    ? chieftainships.filter(c => c.province === activeProvince.name)
    : [];

  function handleProvinceClick(provinceId: string) {
    if (selectedProvince === provinceId) {
      setSelectedProvince(null);
      setSelectedChiefdom(null);
    } else {
      setSelectedProvince(provinceId);
      setSelectedChiefdom(null);
    }
  }

  function handleChiefdomClick(chiefdom: ChieftainshipRow) {
    setSelectedChiefdom(selectedChiefdom?.id === chiefdom.id ? null : chiefdom);
  }

  const uniqueDistricts = useMemo(() => {
    const districts = new Set(chieftainships.map(c => c.district));
    return districts.size;
  }, [chieftainships]);

  const uniqueLineages = useMemo(() => {
    const lineages = new Set(
      chieftainships.map(c => c.clan_lineage.split('—')[0].trim().split('(')[0].trim())
    );
    return lineages.size;
  }, [chieftainships]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background)' }}>
      {/* ── Hero ── */}
      <div
        className="relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #1a0a00 0%, #3d1a00 40%, #6b2d00 70%, #8B4513 100%)',
        }}
      >
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="chiefdom-pattern" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
                <polygon points="30,5 55,20 55,40 30,55 5,40 5,20" fill="none" stroke="#CC7700" strokeWidth="1" />
                <circle cx="30" cy="30" r="4" fill="#CC7700" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#chiefdom-pattern)" />
          </svg>
        </div>

        <div className="relative max-w-screen-xl mx-auto px-6 py-16">
          <div className="flex items-start gap-4 mb-6">
            <div
              className="p-3 rounded-xl"
              style={{ backgroundColor: 'rgba(204,119,0,0.25)', border: '1px solid rgba(204,119,0,0.4)' }}
            >
              <Globe2 size={28} style={{ color: '#F5A623' }} />
            </div>
            <div>
              <p className="text-xs font-semibold tracking-widest uppercase mb-1" style={{ color: '#F5A623' }}>
                Traditional Leadership · Zimbabwe
              </p>
              <h1 className="text-3xl sm:text-4xl font-bold leading-tight" style={{ color: '#FFFFFF', fontFamily: 'var(--font-sans)' }}>
                Chiefly Domains
                <span className="block text-2xl sm:text-3xl font-normal mt-1" style={{ color: '#F5C87A' }}>
                  Matunhu eMuZimbabwe
                </span>
              </h1>
            </div>
          </div>

          <p className="max-w-2xl text-base leading-relaxed mb-6" style={{ color: '#E8D5B0' }}>
            An interactive map of major traditional chieftainship domains across Zimbabwe, mapped to their
            corresponding administrative districts and historical territories.
          </p>

          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium"
              style={{ backgroundColor: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)', color: '#86efac' }}
            >
              <CheckCircle2 size={12} />
              Live Verified Data
            </div>
            {!loading && (
              <div
                className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium"
                style={{ backgroundColor: 'rgba(245,166,35,0.15)', border: '1px solid rgba(245,166,35,0.3)', color: '#F5C87A' }}
              >
                <MapPin size={12} />
                {chieftainships.length} chieftainships loaded
              </div>
            )}
          </div>

          <div
            className="flex gap-3 p-4 rounded-xl max-w-2xl"
            style={{
              backgroundColor: 'rgba(245,166,35,0.12)',
              border: '1px solid rgba(245,166,35,0.3)',
            }}
          >
            <Info size={18} className="flex-shrink-0 mt-0.5" style={{ color: '#F5A623' }} />
            <p className="text-sm leading-relaxed" style={{ color: '#E8D5B0' }}>
              <strong style={{ color: '#F5C87A' }}>Note on Boundaries:</strong> Traditional chiefdom boundaries are defined by
              natural features — rivers, hills, valleys — and historical clan lineage rather than rigid Cartesian GIS
              coordinates. This map represents schematic approximations of territorial domains for educational purposes.
              Actual boundaries are fluid, contested, and subject to ongoing traditional governance processes.
            </p>
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-8">

        {/* Error state */}
        {error && (
          <div
            className="mb-6 flex items-center gap-3 p-4 rounded-xl"
            style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }}
          >
            <AlertCircle size={18} style={{ color: '#DC2626', flexShrink: 0 }} />
            <div className="flex-1">
              <p className="text-sm font-medium" style={{ color: '#DC2626' }}>Failed to load chieftainships</p>
              <p className="text-xs mt-0.5" style={{ color: '#EF4444' }}>{error}</p>
            </div>
            <button
              onClick={loadChieftainships}
              className="text-xs px-3 py-1.5 rounded-lg font-medium"
              style={{ backgroundColor: '#DC2626', color: '#FFFFFF' }}
            >
              Retry
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

          {/* ── Left: Map ── */}
          <div className="xl:col-span-2">
            <div
              className="rounded-2xl overflow-hidden"
              style={{
                border: '1px solid var(--border)',
                backgroundColor: 'var(--card)',
                boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
              }}
            >
              <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border)' }}>
                <div>
                  <h2 className="font-semibold text-base" style={{ color: 'var(--foreground)' }}>
                    Provincial Map of Chiefdoms
                  </h2>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                    Click a province to explore its chiefdoms
                  </p>
                </div>
                {selectedProvince && (
                  <button
                    onClick={() => { setSelectedProvince(null); setSelectedChiefdom(null); }}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors"
                    style={{ color: 'var(--muted-foreground)', backgroundColor: 'var(--muted)' }}
                  >
                    <X size={12} /> Clear
                  </button>
                )}
              </div>

              {/* SVG Map */}
              <div className="p-4">
                <svg
                  viewBox="0 0 600 500"
                  className="w-full"
                  style={{ maxHeight: '480px' }}
                  aria-label="Schematic map of Zimbabwe provinces showing chiefdom locations"
                >
                  <rect width="600" height="500" fill="#F8F4EE" rx="8" />

                  {PROVINCES.map(province => {
                    const shape = PROVINCE_SHAPES[province.id];
                    const colors = PROVINCE_COLORS[province.id] || { fill: '#F0EDE8', text: '#555', border: '#999' };
                    const isSelected = selectedProvince === province.id;
                    const isHovered = hoveredProvince === province.id;
                    if (!shape) return null;
                    return (
                      <g key={province.id}>
                        <polygon
                          points={shape.points}
                          fill={isSelected ? colors.border : isHovered ? colors.fill : '#F0EDE8'}
                          stroke={isSelected ? colors.border : colors.border}
                          strokeWidth={isSelected ? 2.5 : 1.5}
                          style={{
                            cursor: 'pointer',
                            transition: 'fill 0.2s, stroke-width 0.2s',
                            opacity: selectedProvince && !isSelected ? 0.5 : 1,
                          }}
                          onClick={() => handleProvinceClick(province.id)}
                          onMouseEnter={() => setHoveredProvince(province.id)}
                          onMouseLeave={() => setHoveredProvince(null)}
                        />
                        <text
                          x={shape.labelX}
                          y={shape.labelY}
                          textAnchor="middle"
                          fontSize={province.id === 'harare' || province.id === 'bulawayo' ? '6' : '8'}
                          fontWeight={isSelected ? '700' : '500'}
                          fill={isSelected ? '#FFFFFF' : colors.text}
                          style={{ pointerEvents: 'none', userSelect: 'none' }}
                        >
                          {province.name.replace('Matabeleland ', 'Mat. ').replace('Mashonaland ', 'Mash. ')}
                        </text>
                      </g>
                    );
                  })}

                  {/* Chiefdom dots — only render when not loading */}
                  {!loading && chieftainships.map(chiefdom => {
                    const cx = (chiefdom.coord_x / 100) * 600;
                    const cy = (chiefdom.coord_y / 100) * 500;
                    const isSelected = selectedChiefdom?.id === chiefdom.id;
                    const provinceId = PROVINCE_NAME_TO_ID[chiefdom.province];
                    const colors = provinceId ? PROVINCE_COLORS[provinceId] : { fill: '#F0EDE8', text: '#555', border: '#999' };
                    const isFiltered = filteredChiefdoms.some(c => c.id === chiefdom.id);
                    return (
                      <g key={chiefdom.id} style={{ cursor: 'pointer' }} onClick={() => handleChiefdomClick(chiefdom)}>
                        <circle
                          cx={cx}
                          cy={cy}
                          r={isSelected ? 8 : 5}
                          fill={isSelected ? '#CC0000' : colors.border}
                          stroke="#FFFFFF"
                          strokeWidth={isSelected ? 2.5 : 1.5}
                          opacity={isFiltered ? 1 : 0.25}
                          style={{ transition: 'r 0.15s, opacity 0.2s' }}
                        />
                        {isSelected && (
                          <circle cx={cx} cy={cy} r={14} fill="none" stroke="#CC0000" strokeWidth="1.5" opacity="0.4" />
                        )}
                      </g>
                    );
                  })}

                  {/* Loading overlay dots */}
                  {loading && (
                    <g>
                      <rect x="220" y="210" width="160" height="50" rx="8" fill="rgba(255,255,255,0.9)" />
                      <text x="300" y="232" textAnchor="middle" fontSize="11" fill="#8B4513" fontWeight="600">Loading chieftainships…</text>
                      <text x="300" y="250" textAnchor="middle" fontSize="9" fill="#999">Fetching live data</text>
                    </g>
                  )}

                  {/* Compass rose */}
                  <g transform="translate(560, 460)">
                    <circle cx="0" cy="0" r="16" fill="white" stroke="#DDD" strokeWidth="1" />
                    <text x="0" y="-8" textAnchor="middle" fontSize="8" fontWeight="700" fill="#555">N</text>
                    <line x1="0" y1="-5" x2="0" y2="5" stroke="#555" strokeWidth="1.5" />
                    <line x1="-5" y1="0" x2="5" y2="0" stroke="#555" strokeWidth="1" />
                  </g>

                  <text x="10" y="490" fontSize="7" fill="#999">Schematic representation — not to scale</text>
                </svg>
              </div>

              {/* Province legend */}
              <div className="px-5 pb-5">
                <p className="text-xs font-semibold mb-3 uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>
                  Provinces
                </p>
                <div className="flex flex-wrap gap-2">
                  {PROVINCES.map(province => {
                    const colors = PROVINCE_COLORS[province.id] || { fill: '#F0EDE8', text: '#555', border: '#999' };
                    const isActive = selectedProvince === province.id;
                    return (
                      <button
                        key={province.id}
                        onClick={() => handleProvinceClick(province.id)}
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all"
                        style={{
                          backgroundColor: isActive ? colors.border : colors.fill,
                          color: isActive ? '#FFFFFF' : colors.text,
                          border: `1px solid ${colors.border}`,
                        }}
                      >
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: isActive ? '#FFFFFF' : colors.border }}
                        />
                        {province.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* ── Right: Panel ── */}
          <div className="flex flex-col gap-4">
            {/* Search & filter */}
            <div
              className="rounded-2xl p-4"
              style={{ border: '1px solid var(--border)', backgroundColor: 'var(--card)' }}
            >
              <div className="relative mb-3">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
                <input
                  type="text"
                  placeholder="Search chiefdoms, districts, clans…"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm rounded-lg outline-none"
                  style={{
                    backgroundColor: 'var(--input)',
                    border: '1px solid var(--border)',
                    color: 'var(--foreground)',
                  }}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: 'var(--muted-foreground)' }}
                  >
                    <X size={13} />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Filter size={13} style={{ color: 'var(--muted-foreground)' }} />
                <select
                  value={filterProvince}
                  onChange={e => setFilterProvince(e.target.value)}
                  className="flex-1 text-xs py-1.5 px-2 rounded-lg outline-none"
                  style={{
                    backgroundColor: 'var(--input)',
                    border: '1px solid var(--border)',
                    color: 'var(--foreground)',
                  }}
                >
                  <option value="all">All Provinces</option>
                  {PROVINCES.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  {loading ? (
                    <span className="flex items-center gap-1">
                      <Loader2 size={11} className="animate-spin" /> Loading…
                    </span>
                  ) : (
                    `${filteredChiefdoms.length} of ${chieftainships.length} chiefdoms shown`
                  )}
                </p>
                {!loading && chieftainships.length > 0 && (
                  <span
                    className="flex items-center gap-1 text-xs"
                    style={{ color: '#16a34a' }}
                  >
                    <CheckCircle2 size={11} /> Live
                  </span>
                )}
              </div>
            </div>

            {/* Selected chiefdom detail */}
            {selectedChiefdom ? (
              <div
                className="rounded-2xl overflow-hidden flex-1"
                style={{ border: '1px solid var(--border)', backgroundColor: 'var(--card)' }}
              >
                <div
                  className="px-5 py-4"
                  style={{ background: 'linear-gradient(135deg, #3d1a00, #6b2d00)' }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-xs font-semibold tracking-widest uppercase mb-1" style={{ color: '#F5A623' }}>
                        Chiefdom
                      </p>
                      <h3 className="text-lg font-bold" style={{ color: '#FFFFFF' }}>
                        {selectedChiefdom.name}
                      </h3>
                      {selectedChiefdom.alternate_names?.length > 0 && (
                        <p className="text-xs mt-0.5" style={{ color: '#E8D5B0' }}>
                          {selectedChiefdom.alternate_names.join(' · ')}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => setSelectedChiefdom(null)}
                      className="p-1.5 rounded-lg"
                      style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#E8D5B0' }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <span
                      className="text-xs px-2.5 py-1 rounded-full font-medium"
                      style={{ backgroundColor: 'rgba(245,166,35,0.2)', color: '#F5C87A', border: '1px solid rgba(245,166,35,0.3)' }}
                    >
                      {selectedChiefdom.province}
                    </span>
                    <span
                      className="text-xs px-2.5 py-1 rounded-full font-medium"
                      style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#E8D5B0', border: '1px solid rgba(255,255,255,0.15)' }}
                    >
                      {selectedChiefdom.district} District
                    </span>
                    {selectedChiefdom.verified && (
                      <span
                        className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium"
                        style={{ backgroundColor: 'rgba(34,197,94,0.15)', color: '#86efac', border: '1px solid rgba(34,197,94,0.3)' }}
                      >
                        <CheckCircle2 size={10} /> Verified
                      </span>
                    )}
                  </div>
                </div>

                <div className="p-5 space-y-4">
                  <DetailRow icon={<Landmark size={15} />} label="Historical Territory" value={selectedChiefdom.historical_territory} />
                  <DetailRow icon={<Users size={15} />} label="Clan Lineage & Totem" value={selectedChiefdom.clan_lineage} />
                  <DetailRow icon={<TreePine size={15} />} label="Natural Boundaries" value={selectedChiefdom.natural_boundaries} />
                  <DetailRow icon={<BookOpen size={15} />} label="Historical Era" value={selectedChiefdom.era} />
                  <div>
                    <p className="text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>
                      Historical Notes
                    </p>
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--foreground)' }}>
                      {selectedChiefdom.historical_notes}
                    </p>
                  </div>
                </div>
              </div>
            ) : activeProvince ? (
              /* Province chiefdoms list */
              <div
                className="rounded-2xl overflow-hidden flex-1"
                style={{ border: '1px solid var(--border)', backgroundColor: 'var(--card)' }}
              >
                <div className="px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
                  <h3 className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>
                    {activeProvince.name}
                  </h3>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                    {provinceChiefdoms.length} chiefdoms · {activeProvince.districts.length} districts
                  </p>
                </div>
                <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
                  {provinceChiefdoms.length === 0 && !loading ? (
                    <div className="px-5 py-8 text-center">
                      <MapPin size={28} className="mx-auto mb-2" style={{ color: 'var(--muted-foreground)' }} />
                      <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>No chiefdoms found for this province</p>
                    </div>
                  ) : (
                    provinceChiefdoms.map(chiefdom => (
                      <button
                        key={chiefdom.id}
                        onClick={() => handleChiefdomClick(chiefdom)}
                        className="w-full px-5 py-3.5 text-left flex items-center justify-between gap-3 transition-colors hover:bg-[var(--muted)]"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate" style={{ color: 'var(--foreground)' }}>
                            {chiefdom.name}
                          </p>
                          <p className="text-xs truncate mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                            {chiefdom.district} · {chiefdom.clan_lineage.split('—')[0].trim()}
                          </p>
                        </div>
                        <ChevronRight size={14} style={{ color: 'var(--muted-foreground)', flexShrink: 0 }} />
                      </button>
                    ))
                  )}
                </div>
              </div>
            ) : (
              /* Default: chiefdoms list */
              <div
                className="rounded-2xl overflow-hidden flex-1"
                style={{ border: '1px solid var(--border)', backgroundColor: 'var(--card)' }}
              >
                <div className="px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
                  <h3 className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>
                    {searchQuery || filterProvince !== 'all' ? 'Search Results' : 'All Chiefdoms'}
                  </h3>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                    {loading ? 'Loading live data…' : 'Click a chiefdom to view details'}
                  </p>
                </div>
                <div className="overflow-y-auto" style={{ maxHeight: '420px' }}>
                  {loading ? (
                    <div className="px-5 py-10 flex flex-col items-center gap-3">
                      <Loader2 size={28} className="animate-spin" style={{ color: '#8B4513' }} />
                      <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Loading verified chieftainships…</p>
                    </div>
                  ) : filteredChiefdoms.length === 0 ? (
                    <div className="px-5 py-8 text-center">
                      <MapPin size={28} className="mx-auto mb-2" style={{ color: 'var(--muted-foreground)' }} />
                      <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>No chiefdoms found</p>
                    </div>
                  ) : (
                    <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
                      {filteredChiefdoms.map(chiefdom => {
                        const provinceId = PROVINCE_NAME_TO_ID[chiefdom.province];
                        const colors = provinceId ? PROVINCE_COLORS[provinceId] : { fill: '#F0EDE8', text: '#555', border: '#999' };
                        return (
                          <button
                            key={chiefdom.id}
                            onClick={() => handleChiefdomClick(chiefdom)}
                            className="w-full px-5 py-3.5 text-left flex items-center justify-between gap-3 transition-colors hover:bg-[var(--muted)]"
                          >
                            <div className="min-w-0 flex items-center gap-3">
                              <span
                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: colors.border }}
                              />
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate" style={{ color: 'var(--foreground)' }}>
                                  {chiefdom.name}
                                </p>
                                <p className="text-xs truncate mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                                  {chiefdom.district} · {chiefdom.province}
                                </p>
                              </div>
                            </div>
                            <ChevronRight size={14} style={{ color: 'var(--muted-foreground)', flexShrink: 0 }} />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Stats strip ── */}
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Provinces', value: PROVINCES.length.toString(), icon: <Globe2 size={18} /> },
            { label: 'Chiefdoms Mapped', value: loading ? '…' : chieftainships.length.toString(), icon: <MapPin size={18} /> },
            { label: 'Districts Covered', value: loading ? '…' : `${uniqueDistricts}+`, icon: <Landmark size={18} /> },
            { label: 'Clan Lineages', value: loading ? '…' : `${uniqueLineages}+`, icon: <Users size={18} /> },
          ].map(stat => (
            <div
              key={stat.label}
              className="rounded-xl p-4 flex items-center gap-3"
              style={{ border: '1px solid var(--border)', backgroundColor: 'var(--card)' }}
            >
              <div
                className="p-2 rounded-lg flex-shrink-0"
                style={{ backgroundColor: '#FDF3E7', color: '#8B4513' }}
              >
                {stat.icon}
              </div>
              <div>
                <p className="text-xl font-bold" style={{ color: 'var(--foreground)' }}>{stat.value}</p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── About section ── */}
        <div
          className="mt-6 rounded-2xl p-6"
          style={{ border: '1px solid var(--border)', backgroundColor: 'var(--card)' }}
        >
          <h2 className="text-base font-semibold mb-3" style={{ color: 'var(--foreground)' }}>
            About Traditional Chieftainship in Zimbabwe
          </h2>
          <div className="grid sm:grid-cols-2 gap-6 text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
            <p>
              Zimbabwe&apos;s traditional chieftainship system (Ushe) is one of the oldest forms of governance on the continent.
              Chiefs (Mambo/Ishe) are custodians of land, culture, and oral history. Each chiefdom is defined by its totem
              (mutupo), clan lineage, and natural landmarks rather than surveyed boundaries.
            </p>
            <p>
              The major ethnic groupings — Shona (Karanga, Korekore, Manyika, Zezuru, Ndau, Kalanga), Ndebele, Tonga, Venda,
              and others — each have distinct chieftainship traditions. This map represents a living, evolving record of
              these domains as understood through oral tradition and historical scholarship.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Helper component ─────────────────────────────────────────────────────────
function DetailRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <span style={{ color: '#8B4513' }}>{icon}</span>
        <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>
          {label}
        </p>
      </div>
      <p className="text-sm leading-relaxed" style={{ color: 'var(--foreground)' }}>{value}</p>
    </div>
  );
}
